import type { DateTime } from "luxon";
import { getPtoStaffingDays, getStaffingDate, resolveDayCell } from "../../../../utils/dateUtils";
import { getNurseEffectiveStatus, getNurseStatusEndDate, getNurseStatusStartDate } from "../../../../utils/formatters";
import {
	type ActualNurse,
	type CalledOffShift,
	type DayOff,
	type Nurse,
	type NurseInShift,
	Preferences,
	type SpecificPreference,
	type WeekData,
} from "../../../../utils/models";
import { shouldHideScheduledShiftForStatus } from "./scheduledShiftVisibility";

export const DAYS_PER_WEEK = 7;

/** Shared empty result for a day with no call-ins — one identity, so cells don't churn on every resolve. */
const NO_CALL_INS: CalledOffShift[] = [];

/** One visible row of the profile week grid: a (possibly non-Sunday) week start plus its data-week index. */
export type WeekMeta = {
	weekStart: DateTime;
	viewOnly: boolean;
	// Index into the contiguous `weeksData` (`hospitalData.weeks`) of the data-week that contains
	// this row's visible week-start. -1 when no data-week exists for it (cells resolve empty).
	absoluteWeekIndex: number;
};

/**
 * Everything that is constant across a week's 7 columns. Built once per render by the grid and
 * reused by the export, so both surfaces resolve cells from an identical snapshot.
 */
export type WeekScheduleRowContext = {
	// The SINGLE CONTIGUOUS `hospitalData.weeks` array (Sunday-aligned). Cell lookups index this
	// directly via `absoluteWeekIndex`, so the `+1` spillover hits the calendar-next week's data.
	weeksData: WeekData[];
	nurse: Nurse;
	departmentId: string;
	// Days the visible week is shifted past Sunday (the data-week start). 0 = Sunday-aligned
	// (flag off / default) → identity lookups, i.e. unchanged behavior.
	offset: number;
	todayStaffingDate: DateTime;
	todayStr: string;
	dayOffByDateStr: ReadonlyMap<string, DayOff>;
	preferenceByDateStr: ReadonlyMap<string, SpecificPreference>;
	// Call-ins keyed by the staffing day of the shift they removed (see `utils/callIns`). The shift itself
	// is gone from `weeksData` — a call-in soft-deletes it — so this is the ONLY trace of it in the grid.
	callInsByDateStr: ReadonlyMap<string, CalledOffShift[]>;
};

/** Everything one calendar cell of the profile week grid shows, before any presentation. */
export type WeekScheduleCell = {
	currentDate: DateTime;
	currentDayStr: string;
	// Past days and medical-department weeks are read-only: they show actuals instead of editable shifts.
	effectiveViewOnly: boolean;
	// `undefined` (not `[]`) means no data-week is loaded for this cell — the grid renders those cells
	// on a grey "no data" background, so the distinction is load-bearing and must not be normalized away.
	scheduledShifts: NurseInShift[] | undefined;
	actualShifts: ActualNurse[];
	dayOff: DayOff | undefined;
	dayPreference: SpecificPreference | undefined;
	// Empty (not undefined) on a day with no call-ins, so cells never branch on nullability.
	callIns: CalledOffShift[];
};

/** Order a single day's shifts earliest-first so a cell with multiple shifts reads top-to-bottom by time. */
export const byStartTimeAsc = (a: { startTime: DateTime } | undefined, b: { startTime: DateTime } | undefined): number =>
	(a?.startTime.toMillis() ?? 0) - (b?.startTime.toMillis() ?? 0);

/**
 * Map every day covered by a PTO entry to that entry, keyed "yyyy-MM-dd" in dept staffing-date space.
 * Avoids re-running `daysOff.find(... setZone + getStaffingDate + toFormat ...)` per cell.
 */
export const buildDayOffByDateStr = (daysOff: DayOff[], timezone: string, dayStart: string): Map<string, DayOff> => {
	const map = new Map<string, DayOff>();
	for (const off of daysOff) {
		for (const day of getPtoStaffingDays(off.dateFrom.setZone(timezone), off.dateTo.setZone(timezone), dayStart)) {
			map.set(day.toFormat("yyyy-MM-dd"), off);
		}
	}
	return map;
};

/** Map the nurse's "prefer" preferences by their start day, keyed "yyyy-MM-dd". */
export const buildPreferenceByDateStr = (preferences: SpecificPreference[]): Map<string, SpecificPreference> => {
	const map = new Map<string, SpecificPreference>();
	for (const pref of preferences) {
		if (pref.type !== Preferences.Prefer) continue;
		map.set(pref.start_date.startOf("day").toFormat("yyyy-MM-dd"), pref);
	}
	return map;
};

/**
 * Resolve one visible column of one visible week.
 *
 * `week.weekStart` is already the visible (possibly non-Sunday) week start, so the calendar date is
 * correct as-is. The SHIFT DATA, however, is Sunday-indexed in the contiguous `weeksData`, so the
 * visible column is mapped → Sunday-indexed source cell (spilling into the NEXT contiguous data-week
 * when the column overflows past Saturday). Because the lookup uses the absolute contiguous index,
 * `+1` is always the true calendar-next week. offset=0 → identity → unchanged behavior.
 */
export const resolveWeekScheduleCell = (week: WeekMeta, dayIndex: number, context: WeekScheduleRowContext): WeekScheduleCell => {
	const { weeksData, nurse, departmentId, offset, todayStaffingDate, todayStr, dayOffByDateStr, preferenceByDateStr, callInsByDateStr } = context;
	const currentDate = week.weekStart.plus({ day: dayIndex });
	const currentDayStr = currentDate.toFormat("yyyy-MM-dd");
	const isPastDay = currentDayStr < todayStr;
	const effectiveViewOnly = week.viewOnly || isPastDay;

	const { sourceWeekIndex, sourceDayIndex } = resolveDayCell(week.absoluteWeekIndex, dayIndex, offset);

	// Derived from the freshly-refreshed status rather than the schedule payload, so a terminated
	// employee's lingering future shifts disappear immediately (see scheduledShiftVisibility.ts).
	const hideScheduledShift = shouldHideScheduledShiftForStatus({
		status: getNurseEffectiveStatus(nurse, departmentId),
		currentDate,
		today: todayStaffingDate,
		start: getNurseStatusStartDate(nurse, departmentId),
		end: getNurseStatusEndDate(nurse, departmentId),
	});

	const scheduledShifts = hideScheduledShift
		? []
		: weeksData[sourceWeekIndex]?.weeklyShifts
				.flatMap((shift) => shift.days[sourceDayIndex]?.nurses)
				.filter(
					(nurseInShift): nurseInShift is NurseInShift =>
						nurseInShift != null && nurseInShift.nurseId === nurse.id && !nurseInShift.isPending && !nurseInShift.isPendingToDelete,
				)
				.sort(byStartTimeAsc);

	const actualShifts: ActualNurse[] = effectiveViewOnly
		? (weeksData[sourceWeekIndex]?.weeklyShifts
				.flatMap((shift) => shift.days[sourceDayIndex]?.nursesActual ?? [])
				.filter((actualNurse) => actualNurse != null && actualNurse.nurseId === nurse.id && !actualNurse.isPending && !actualNurse.isPendingToDelete)
				.sort(byStartTimeAsc) ?? [])
		: [];

	return {
		currentDate,
		currentDayStr,
		effectiveViewOnly,
		scheduledShifts,
		actualShifts,
		dayOff: dayOffByDateStr.get(currentDayStr),
		dayPreference: scheduledShifts?.length ? undefined : preferenceByDateStr.get(currentDayStr),
		callIns: callInsByDateStr.get(currentDayStr) ?? NO_CALL_INS,
	};
};

/** Resolve all 7 visible columns of one visible week. Always returns exactly 7 cells. */
export const resolveWeekScheduleRow = (week: WeekMeta, context: WeekScheduleRowContext): WeekScheduleCell[] =>
	Array.from({ length: DAYS_PER_WEEK }, (_, dayIndex) => resolveWeekScheduleCell(week, dayIndex, context));
